<template>
  <div class="kh-metro-page">
    <!-- Header -->
    <el-menu mode="horizontal" class="kh-header">
      <el-menu-item class="kh-header__logo-item">
        <img class="kh-header__logo" :src="require('@/assets/kmrt/header-logo.png')" alt="高雄市政府捷運工程局" />
        <span class="kh-header__title">施工中大地監測資料公開</span>
      </el-menu-item>
      <div class="kh-header__right">
        <el-menu-item>
          <el-dropdown @command="onLineSwitch" placement="bottom" trigger="hover">
            <div class="flex-row align-center">
              <span class="el-dropdown-link">{{ line.name }}</span>
              <i class="el-icon-arrow-down el-icon--right"></i>
            </div>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item command="RL">小港林園線</el-dropdown-item>
              <el-dropdown-item command="Y">黃線</el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </el-menu-item>
      </div>
    </el-menu>

    <!-- Route Bar -->
    <div class="kh-route-bar">
      <div class="kh-route-img-wrapper">
        <img :src="line.lineImage" class="kh-route-img" alt="路線圖" draggable="false" />
        <div
          v-for="node in line.nodes"
          :key="node.id"
          class="kh-node-hit"
          :class="[
            `kh-node-hit--${node.imgShape}`,
            { 'kh-node-hit--active': selectedNodeId === node.id }
          ]"
          :style="{
            left: node.imgPos.left,
            top: node.imgPos.top,
            '--lc': line.color,
            width: node.imgPos.w || null,
            height: node.imgPos.h || null,
          }"
          :title="node.label"
          @click="selectNode(node.id)"
        ></div>
      </div>
    </div>

    <!-- Body: Map + Table -->
    <div class="kh-body">
      <div class="kh-map-panel">
        <div class="kh-map-wrapper" ref="mapWrapper">
          <img
            ref="mapImg"
            :src="line.mapImage"
            class="kh-map-img"
            alt="路線地圖"
            draggable="false"
            @load="updateImgRect"
          />
          <div
            v-if="selectedNode && selectedNode.mapPos && imgRect.renderedW"
            class="kh-map-marker"
            :style="markerStyle"
          ></div>
        </div>
      </div>
      <div class="kh-data">
        <h2 class="kh-data__title" :style="{ color: line.color }">{{ selectedNode ? selectedNode.label : '' }}</h2>
        <div v-if="dataLoading" class="kh-no-data">資料載入中…</div>
        <div v-else-if="dataError" class="kh-no-data">監測資料載入失敗，請稍後再試</div>
        <template v-else-if="selectedNode && selectedNode.measurements && selectedNode.measurements.length">
          <table class="kh-table">
            <thead>
              <tr>
                <th>觀測項目</th>
                <th>儀器編號</th>
                <th>觀測結果<br><small>（警戒值 / 行動值）</small></th>
                <th>測值結果</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in selectedNode.measurements" :key="row.label">
                <td>{{ row.label }}</td>
                <td class="kh-table__mono">{{ row.instrumentId || '—' }}</td>
                <td v-if="row.status === 'down'">
                  <span class="kh-val kh-val--down">無資料</span>
                </td>
                <td v-else>
                  <span :class="`kh-val kh-val--${statusClass(row.status)}`">
                    {{ row.value > 0 ? '+' : '' }}{{ row.value.toFixed(3) }} {{ row.unit }}
                  </span>
                  <div class="kh-limits">（{{ row.alertText }} / {{ row.actionText }}）</div>
                </td>
                <td>
                  <span :class="`kh-status kh-status--${statusClass(row.status)}`">{{ statusLabel(row.status) }}</span>
                </td>
              </tr>
              <tr>
                <td>結論說明</td>
                <td colspan="3" class="kh-table__conclusion">{{ conclusionText }}</td>
              </tr>
            </tbody>
          </table>
        </template>
        <div v-else class="kh-no-data">此節點無監測資料</div>

        <div class="kh-legend">
          <span class="kh-legend__item kh-legend__item--normal">■ 無異常</span>
          <span class="kh-legend__item kh-legend__item--warning">■ 達注意值</span>
          <span class="kh-legend__item kh-legend__item--alert">■ 達警戒值</span>
          <span class="kh-legend__item kh-legend__item--action">■ 達行動值</span>
          <span class="kh-legend__item kh-legend__item--down">■ 無資料</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { KH_LINES, PUBLIC_DATA_URL, flattenPublicInstruments, buildMeasurementsForPrefix } from '@/constants/kh-metro';

export default {
  name: 'KhMetroIndex',
  data() {
    return {
      lineKey: this.$route.params.lineName || 'RL',
      selectedNodeId: null,
      // Actual rendered image rect within .kh-map-wrapper (accounts for object-fit:contain letterboxing)
      imgRect: { offsetX: 0, offsetY: 0, renderedW: 0, renderedH: 0 },
      rawInstruments: [],
      dataLoading: true,
      dataError: false,
    };
  },
  computed: {
    line() {
      return KH_LINES[this.lineKey] || KH_LINES.RL;
    },
    selectedNode() {
      if (!this.selectedNodeId) return null;
      const node = this.line.nodes.find(n => n.id === this.selectedNodeId);
      if (!node) return null;
      return {
        ...node,
        measurements: buildMeasurementsForPrefix(this.rawInstruments, node.measurementPrefix),
      };
    },
    markerStyle() {
      const { offsetX, offsetY, renderedW, renderedH } = this.imgRect;
      const leftRatio = parseFloat(this.selectedNode.mapPos.left) / 100;
      const topRatio = parseFloat(this.selectedNode.mapPos.top) / 100;
      const size = Math.min(Math.max(renderedW * 0.025, 10), 22);
      return {
        left: `${offsetX + leftRatio * renderedW}px`,
        top: `${offsetY + topRatio * renderedH}px`,
        width: `${size}px`,
        height: `${size}px`,
        '--lc': this.line.color,
        '--ring1': `${(size / 6).toFixed(1)}px`,
        '--ring2': `${(size / 3).toFixed(1)}px`,
      };
    },
    conclusionText() {
      if (!this.selectedNode || !this.selectedNode.measurements.length) return '';
      const statuses = this.selectedNode.measurements.map(r => r.status);
      if (statuses.includes('WSL')) return '部分儀器觀測值已超過行動值，請關注後續變化。';
      if (statuses.includes('AL')) return '部分儀器觀測值已超過警戒值，請加強監測。';
      if (statuses.includes('WL')) return '部分儀器觀測值已達注意值，請留意後續變化。';
      return '各項觀測值均在正常範圍內。';
    },
  },
  watch: {
    '$route.params.lineName'(val) {
      this.lineKey = val || 'RL';
      this.selectedNodeId = this.line.defaultNodeId;
    },
  },
  mounted() {
    this.lineKey = this.$route.params.lineName || 'RL';
    this.selectedNodeId = this.line.defaultNodeId;
    this.resizeObserver = new ResizeObserver(() => this.updateImgRect());
    if (this.$refs.mapWrapper) this.resizeObserver.observe(this.$refs.mapWrapper);
    this.fetchPublicData();

    // ▼▼▼ [MAP-CALIBRATE] 地圖點位校準工具（開發除錯用）—— 部署前請整段註解掉 ▼▼▼
    // 用法：滑鼠點在地圖上想要標記的位置，該點的 left%/top% 會印到 console
    // 並複製到剪貼簿，同時在滑鼠旁短暫顯示，可直接貼到 kh-metro.js 的 mapPos。
    this._mapCalibrateHandler = e => {
      const { offsetX, offsetY, renderedW, renderedH } = this.imgRect;
      if (!renderedW || !this.$refs.mapWrapper) return;
      const wrapperRect = this.$refs.mapWrapper.getBoundingClientRect();
      const leftPct = (((e.clientX - wrapperRect.left) - offsetX) / renderedW * 100).toFixed(1);
      const topPct = (((e.clientY - wrapperRect.top) - offsetY) / renderedH * 100).toFixed(1);
      const text = `left: '${leftPct}%', top: '${topPct}%'`;
      console.log('[map-calibrate]', text);
      if (navigator.clipboard) navigator.clipboard.writeText(text).catch(() => { /* clipboard unavailable, ignore */ });
      const badge = document.createElement('div');
      badge.textContent = text;
      badge.style.cssText = 'position:fixed;z-index:9999;background:#000;color:#0f0;'
        + 'font:12px monospace;padding:4px 8px;border-radius:4px;pointer-events:none;white-space:nowrap;';
      badge.style.left = `${e.clientX + 12}px`;
      badge.style.top = `${e.clientY + 12}px`;
      document.body.appendChild(badge);
      setTimeout(() => badge.remove(), 2500);
    };
    if (this.$refs.mapWrapper) this.$refs.mapWrapper.addEventListener('click', this._mapCalibrateHandler);
    // ▲▲▲ [MAP-CALIBRATE] ▲▲▲
  },
  beforeDestroy() {
    if (this.resizeObserver) this.resizeObserver.disconnect();

    // ▼▼▼ [MAP-CALIBRATE] 部署前請整段註解掉（需與 mounted() 內的區塊一起移除） ▼▼▼
    if (this._mapCalibrateHandler && this.$refs.mapWrapper) {
      this.$refs.mapWrapper.removeEventListener('click', this._mapCalibrateHandler);
    }
    // ▲▲▲ [MAP-CALIBRATE] ▲▲▲
  },
  methods: {
    fetchPublicData() {
      this.dataLoading = true;
      this.dataError = false;
      return axios.get(PUBLIC_DATA_URL)
        .then(res => {
          this.rawInstruments = flattenPublicInstruments(res.data);
        })
        .catch(() => {
          this.dataError = true;
        })
        .finally(() => {
          this.dataLoading = false;
        });
    },
    updateImgRect() {
      const img = this.$refs.mapImg;
      const wrapper = this.$refs.mapWrapper;
      if (!img || !wrapper || !img.naturalWidth || !img.naturalHeight) return;
      const wrapperW = wrapper.clientWidth;
      const wrapperH = wrapper.clientHeight;
      const scale = Math.min(wrapperW / img.naturalWidth, wrapperH / img.naturalHeight);
      const renderedW = img.naturalWidth * scale;
      const renderedH = img.naturalHeight * scale;
      this.imgRect = {
        offsetX: (wrapperW - renderedW) / 2,
        offsetY: (wrapperH - renderedH) / 2,
        renderedW,
        renderedH,
      };
    },
    selectNode(id) {
      this.selectedNodeId = id;
    },
    onLineSwitch(lineKey) {
      this.$router.push(`/kh-metro/${lineKey}`);
    },
    statusClass(status) {
      return { GREEN: 'normal', WL: 'warning', AL: 'alert', WSL: 'action', down: 'down' }[status] || 'down';
    },
    statusLabel(status) {
      return { GREEN: '無異常', WL: '達注意值', AL: '達警戒值', WSL: '達行動值', down: '無資料/離線' }[status] || '';
    },
  },
};
</script>

<style scoped>
.kh-metro-page {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: #f5f5f5;
  font-family: 'Noto Sans TC', '微軟正黑體', sans-serif;
}

/* Header */
.kh-header {
  flex-shrink: 0;
  box-shadow: 0 0 6px 0 rgba(0,0,0,0.1);
}
.kh-header__logo-item {
  display: flex !important;
  align-items: center;
  gap: 12px;
}
.kh-header__logo {
  height: 60px;
  padding: 5px 0;
  object-fit: contain;
}
.kh-header__title {
  font-size: 15px;
  font-weight: bold;
  color: #333;
  letter-spacing: 0.5px;
  white-space: nowrap;
}
.kh-header__right {
  margin-left: auto;
  display: flex;
  align-items: center;
}

/* Route Bar */
.kh-route-bar {
  background: white;
  border-bottom: 1px solid #e0e0e0;
  padding: 8px 16px;
  flex-shrink: 0;
  overflow-x: auto;
}
.kh-route-img-wrapper {
  position: relative;
  display: inline-block;
  min-width: 900px;
  width: 100%;
  user-select: none;
}
.kh-route-img {
  width: 100%;
  display: block;
  pointer-events: none;
}
/* Transparent hit area, centered on each node via transform */
.kh-node-hit {
  position: absolute;
  transform: translate(-50%, -50%);
  cursor: pointer;
  border-radius: 6px;
  transition: box-shadow 0.15s, background 0.1s;
}
.kh-node-hit:hover {
  background: rgba(0, 0, 0, 0.08);
}
.kh-node-hit--active {
  background: rgba(0, 0, 0, 0.06);
  box-shadow: 0 0 0 3px white, 0 0 0 6px var(--lc);
}

/* Body */
.kh-body {
  display: flex;
  flex: 1;
  overflow: hidden;
  gap: 0;
}
.kh-map-panel {
  flex: 0 0 45%;
  min-height: 300px;
  background: #e8edf0;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}
.kh-map-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
}
.kh-map-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
  pointer-events: none;
}
.kh-map-marker {
  position: absolute;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  background: var(--lc);
  box-shadow: 0 0 0 var(--ring1, 3px) white, 0 0 0 var(--ring2, 6px) var(--lc);
  pointer-events: none;
}
.kh-data {
  flex: 1;
  padding: 20px 24px;
  overflow-y: auto;
  background: white;
  border-left: 1px solid #e0e0e0;
}

.kh-data__title {
  font-size: 20px;
  font-weight: bold;
  margin: 0 0 16px;
}

/* Table */
.kh-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}
.kh-table th {
  background: #f0f0f0;
  padding: 10px 12px;
  text-align: center;
  border: 1px solid #ddd;
  font-weight: bold;
  font-size: 13px;
}
.kh-table td {
  padding: 10px 12px;
  border: 1px solid #ddd;
  text-align: center;
  vertical-align: middle;
}
.kh-table__mono {
  font-family: monospace;
  font-size: 13px;
}
.kh-table__conclusion {
  text-align: left;
  color: #555;
}
.kh-val {
  font-weight: bold;
  font-size: 15px;
}
.kh-val--normal  { color: #333; }
.kh-val--warning { color: #0093c9; }
.kh-val--alert   { color: #E87722; }
.kh-val--action  { color: #E60026; }
.kh-val--down    { color: #999; font-weight: normal; }
.kh-limits {
  font-size: 12px;
  color: #888;
  margin-top: 2px;
}
.kh-status {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: bold;
}
.kh-status--normal  { background: #f0f0f0; color: #333; }
.kh-status--warning { background: #FFF8E1; color: #0093c9; }
.kh-status--alert   { background: #FFF3E0; color: #E87722; }
.kh-status--action  { background: #FFEBEE; color: #E60026; }
.kh-status--down    { background: #F5F5F5; color: #999; }

.kh-legend {
  display: flex;
  gap: 16px;
  margin-top: 16px;
  font-size: 13px;
  flex-wrap: wrap;
}
.kh-legend__item--normal  { color: #333; }
.kh-legend__item--warning { color: #0093c9; }
.kh-legend__item--alert   { color: #E87722; }
.kh-legend__item--action  { color: #E60026; }
.kh-legend__item--down    { color: #999; }

.kh-no-data {
  color: #aaa;
  font-size: 14px;
  margin-top: 20px;
}
</style>
